import './pet-profiles.css'
import '../pet-profile/pet-profile.js'
import { DEFAULT_PET } from '../pet-profile/pet-profile.js'
import BoxState, { EVENT_TYPES } from '../../scripts/lib/box-state.js'
import PetSessionManager from '../../scripts/lib/pet-session-manager.js'
import { processAndApplyRecommendations } from '../../scripts/lib/meal-plan-flow.js'
import { MAX_PETS_PER_MEAL_PLAN } from '../../scripts/lib/constants.js'

class PetProfiles extends HTMLElement {
  #boxState
  #formEl
  #addButtonEl
  #loginLinkEl
  #boundAddNewProfile
  #boundHandleNext
  #boundHandleProfilesNext
  #boundHandlePetProfilesChange
  #isLoadingFromApi = false
  #initialProfilesSnapshot = null

  #serializeForComparison(pets) {
    return JSON.stringify(pets.map(({ profileId, isPuppy, ...rest }) => rest))
  }

  constructor() {
    super()
    this._profiles = []
    this.#boxState = BoxState.getInstance()
    this.#boundAddNewProfile = () => this.addNewProfile()
    this.#boundHandleNext = (e) => this.handleNext(e)
    this.#boundHandleProfilesNext = (e) => this.handleProfilesNext(e)
    this.#boundHandlePetProfilesChange = (pets) => this.handlePetProfilesChange(pets)
  }

  get profiles() {
    return this._profiles
  }

  set profiles(value) {
    this._profiles = value
    this.render()
  }

  connectedCallback() {
    this.#loadStateFromCookie()

    const hasEmail = !!this.#boxState.getCurrentEmail()

    if (this._profiles.length === 0) {
      if (hasEmail) {
        this.#isLoadingFromApi = true
      } else {
        this._profiles = [this.#createNewProfile()]
      }
    }

    if (this.#boxState.samplerStep === 'review' && !this.#isLoadingFromApi) {
      this.#initialProfilesSnapshot = this.#serializeForComparison(this._profiles)
    }

    this.render()
    this.attachEventListeners()
    this.checkLoginStatus()
    this.addEventListener('profiles-next', this.#boundHandleProfilesNext)
    this.#boxState.subscribe(EVENT_TYPES.PET_PROFILES_CHANGE, this.#boundHandlePetProfilesChange)
    this.hidden = false
  }

  #loadStateFromCookie() {
    const savedProfiles = this.#boxState.petProfiles
    if (!savedProfiles || savedProfiles.length === 0) return

    if (Array.isArray(savedProfiles) && savedProfiles.length > 0) {
      this._profiles = savedProfiles.map((petFromCookie, index) => ({
        id: petFromCookie.id || `profile-${Date.now()}-${index}`,
        ...DEFAULT_PET,
        ...petFromCookie
      }))
    }
  }

  #createNewProfile() {
    return {
      id: `profile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...DEFAULT_PET
    }
  }

  disconnectedCallback() {
    this.removeEventListeners()
    this.removeEventListener('profiles-next', this.#boundHandleProfilesNext)
    this.#boxState?.unsubscribe(EVENT_TYPES.PET_PROFILES_CHANGE, this.#boundHandlePetProfilesChange)
  }

  render() {
    this.innerHTML = `
      <div class="pet-profiles-skeleton v:max-w-full v:l:max-w-[32.625rem] v:mx-auto" ${!this.#isLoadingFromApi ? 'hidden' : ''}>
        <div class="pet-profiles-skeleton__card v:relative v:bg-white v:rounded-lg v:px-5 v:py-8 v:l:p-8 v:border v:border-gray-25">
          <div class="v:animate-pulse v:space-y-5">
            <div class="v:grid v:grid-cols-1 v:l:grid-cols-2 v:gap-5">
              <div>
                <div class="v:h-4 v:bg-gray-10 v:rounded v:w-16 v:mb-2"></div>
                <div class="v:h-10 v:bg-gray-10 v:rounded"></div>
              </div>
              <div class="v:flex v:gap-2">
                <div class="v:flex-1">
                  <div class="v:h-4 v:bg-gray-10 v:rounded v:w-12 v:mb-2"></div>
                  <div class="v:h-10 v:bg-gray-10 v:rounded"></div>
                </div>
                <div class="v:flex-1">
                  <div class="v:h-4 v:bg-transparent v:mb-2"></div>
                  <div class="v:h-10 v:bg-gray-10 v:rounded"></div>
                </div>
              </div>
            </div>
            <div class="v:grid v:grid-cols-1 v:l:grid-cols-2 v:gap-5">
              <div>
                <div class="v:h-4 v:bg-gray-10 v:rounded v:w-12 v:mb-2"></div>
                <div class="v:h-16 v:bg-gray-10 v:rounded"></div>
              </div>
              <div>
                <div class="v:h-4 v:bg-gray-10 v:rounded v:w-16 v:mb-2"></div>
                <div class="v:h-10 v:bg-gray-10 v:rounded v:w-20"></div>
              </div>
            </div>
            <div>
              <div class="v:h-4 v:bg-gray-10 v:rounded v:w-24 v:mb-2"></div>
              <div class="v:h-12 v:bg-gray-10 v:rounded"></div>
            </div>
          </div>
        </div>
        <div class="v:mt-5 v:text-center v:text-sm v:text-gray-50">
          Loading your pets...
        </div>
      </div>

      <form class="pet-profiles-form v:max-w-full v:l:max-w-[32.625rem] v:mx-auto" aria-label="Pet profiles" ${this.#isLoadingFromApi ? 'hidden' : ''}>
        <div class="pet-profiles__list v:flex v:flex-col v:gap-5">
        </div>
        <div class="pet-profiles__actions v:mt-5 v:l:mt-6 v:flex v:flex-col v:gap-3 v:items-center">
          <button type="button" class="pet-profiles__add button v:button--secondary v:button--sm v:self-start">
            + Add a Pet
          </button>
        </div>
        <div class="pet-profiles__cta v:mt-6 v:l:mt-7 v:flex v:flex-col v:gap-3 v:items-center">
          <button type="submit" class="pet-profiles__next button v:button--md v:w-full v:max-w-[528px]">
            Next
          </button>
          <a href="/account/login" class="pet-profiles__login-link v:text-base v:text-brand-brown v:underline" hidden>
            Already have an account? Sign in.
          </a>
        </div>
      </form>
    `

    this.#formEl = this.querySelector('form')
    this.#addButtonEl = this.querySelector('.pet-profiles__add')
    this.#loginLinkEl = this.querySelector('.pet-profiles__login-link')

    const profilesList = this.querySelector('.pet-profiles__list')
    if (profilesList) {
      this._profiles.forEach(profile => {
        const petProfile = document.createElement('pet-profile')
        petProfile.dataset.profileId = profile.id

        Object.entries(profile).forEach(([key, value]) => {
          if (key !== 'id' && value !== undefined && value !== null) {
            petProfile.dataset[key] = value
          }
        })

        profilesList.appendChild(petProfile)
      })
    }

    this.updateAddButtonVisibility()
    this.updateCloseButtonVisibility()
  }

  updateRender(fullReplace = false) {
    const profilesList = this.querySelector('.pet-profiles__list')
    if (!profilesList) return

    if (fullReplace) {
      this._fullReplaceProfiles(profilesList)
    } else {
      this._incrementalUpdateProfiles(profilesList)
    }

    this.updateAddButtonVisibility()
    this.updateCloseButtonVisibility()
  }

  _fullReplaceProfiles(profilesList) {
    profilesList.innerHTML = ''
    this._profiles.forEach(profile => {
      const petProfile = this._createPetProfileElement(profile)
      profilesList.appendChild(petProfile)
    })
  }

  _incrementalUpdateProfiles(profilesList) {
    const existingProfilesMap = new Map(
      [...profilesList.querySelectorAll('pet-profile')].map(el => [el.dataset.profileId, el])
    )
    const newIds = new Set(this._profiles.map(p => p.id))

    existingProfilesMap.forEach((profileEl, id) => {
      if (!newIds.has(id)) {
        profileEl.remove()
      }
    })

    this._profiles.forEach(profile => {
      const existingProfile = existingProfilesMap.get(profile.id)

      if (existingProfile) {
        this._syncProfileData(existingProfile, profile)
      } else {
        const petProfile = this._createPetProfileElement(profile)
        profilesList.appendChild(petProfile)
      }
    })
  }

  _createPetProfileElement(profile) {
    const petProfile = document.createElement('pet-profile')
    petProfile.dataset.profileId = profile.id
    this._syncProfileData(petProfile, profile)
    return petProfile
  }

  _syncProfileData(element, profile) {
    Object.entries(profile).forEach(([key, value]) => {
      if (key !== 'id') {
        element.dataset[key] = value ?? ''
      }
    })
  }

  attachEventListeners() {
    this.addEventListener('profile-close', (e) => this.handleProfileClose(e))

    if (this.#addButtonEl) {
      this.#addButtonEl.addEventListener('click', this.#boundAddNewProfile)
    }

    if (this.#formEl) {
      this.#formEl.addEventListener('submit', this.#boundHandleNext)
    }
  }

  removeEventListeners() {
    this.removeEventListener('profile-close', this.handleProfileClose)

    if (this.#addButtonEl) {
      this.#addButtonEl.removeEventListener('click', this.#boundAddNewProfile)
    }

    if (this.#formEl) {
      this.#formEl.removeEventListener('submit', this.#boundHandleNext)
    }
  }

  #parseFormData() {
    const petProfileElements = this.querySelectorAll('pet-profile')
    const petsData = {}

    petProfileElements.forEach(petProfile => {
      const data = petProfile.getData()
      if (data && data.id) {
        petsData[data.id] = data
      }
    })

    return petsData
  }

  addNewProfile() {
    const currentData = this.#parseFormData()

    const updatedProfiles = this._profiles.map(profile => ({
      ...profile,
      ...(currentData[profile.id] || {})
    }))

    this._profiles = [...updatedProfiles, this.#createNewProfile()]

    const validPets = updatedProfiles.filter(pet => pet.name)
    const petSessionManager = PetSessionManager.getInstance()
    petSessionManager.saveToSession(validPets)

    this.updateRender()
  }

  handleProfileClose(event) {
    const { profileId } = event.detail
    const filteredProfiles = this._profiles.filter(p => p.id !== profileId)

    this._profiles = filteredProfiles.length > 0
      ? filteredProfiles
      : [this.#createNewProfile()]

    this.updateRender()
  }

  handlePetProfilesChange(pets) {
    console.log('[PetProfiles] Received API pets from BoxState:', pets?.length || 0)

    if (!this.#isLoadingFromApi) {
      console.log('[PetProfiles] Ignoring PET_PROFILES_CHANGE - user is actively editing')
      return
    }

    this.#isLoadingFromApi = false

    const skeletonEl = this.querySelector('.pet-profiles-skeleton')
    const formEl = this.querySelector('.pet-profiles-form')

    if (skeletonEl) skeletonEl.hidden = true
    if (formEl) formEl.hidden = false

    if (!pets || pets.length === 0) {
      if (this._profiles.length === 0) {
        this._profiles = [this.#createNewProfile()]
        this.updateRender()
      }
      return
    }

    this._profiles = pets.map((pet, index) => ({
      id: pet.id || `profile-${Date.now()}-${index}`,
      ...DEFAULT_PET,
      ...pet
    }))

    if (this.#boxState.samplerStep === 'review' && this.#initialProfilesSnapshot === null) {
      this.#initialProfilesSnapshot = this.#serializeForComparison(this._profiles)
    }

    this.updateRender(true)
  }

  updateAddButtonVisibility() {
    if (this.#addButtonEl) {
      this.#addButtonEl.hidden = this._profiles.length >= MAX_PETS_PER_MEAL_PLAN
    }
  }

  updateCloseButtonVisibility() {
    const petProfiles = this.querySelectorAll('pet-profile')
    const shouldHideClose = petProfiles.length <= 1

    petProfiles.forEach(profile => {
      const closeBtn = profile.querySelector('.pet-profile__close')
      if (closeBtn) {
        closeBtn.hidden = shouldHideClose
      }
    })
  }

  checkLoginStatus() {
    if (this.#loginLinkEl) {
      const isLoggedIn = window.VIVA?.customer?.id
      this.#loginLinkEl.hidden = !!isLoggedIn
    }
  }

  handleNext(event) {
    event.preventDefault()

    if (!this.#formEl) return

    if (!this.#formEl.checkValidity()) {
      this.#formEl.reportValidity()
      return
    }

    const petsData = this.#parseFormData()
    console.log('[PetProfiles] Parsed form data:', petsData)
    const validPets = Object.values(petsData).filter(pet => pet.name)
    console.log('[PetProfiles] Valid pets with names:', validPets)

    if (this.#initialProfilesSnapshot !== null &&
        this.#boxState.samplerStep === 'review' &&
        this.#serializeForComparison(validPets) !== this.#initialProfilesSnapshot) {
      this.#boxState.setSamplerStep('recipe')
      this.#boxState.samplerSelections = []
    }

    this.showLoadingScreen()

    this.dispatchEvent(new CustomEvent('profiles-next', {
      bubbles: true,
      detail: { pets: validPets }
    }))
  }

  showLoadingScreen() {
    if (this.#formEl) {
      this.#formEl.style.display = 'none'
    }

    const section = this.closest('.shopify-section--pet-profiles')
    if (!section) {
      console.warn('Section not found for loading screen')
      return
    }

    const loadingContainer = document.createElement('div')
    loadingContainer.className = 'pet-profiles__loading-container v:absolute v:inset-0 v:z-50 v:min-h-[calc(100vh-124px)] v:flex v:items-center v:justify-center v:bg-white'
    loadingContainer.innerHTML = `
      <div class="v:w-full v:max-w-[375px] v:px-5 v:l:max-w-[667px] v:l:px-0">
        <h2 class="v:text-[22px] v:l:text-[40px] v:font-black v:font-heading v:text-center v:text-brand-brown v:mb-10">
          Give us a moment...
        </h2>
        <div class="v:relative v:h-5 v:rounded-xl v:bg-brand-purple-light v:overflow-hidden">
          <div class="pet-profiles__progress-bar v:absolute v:inset-y-0 v:left-0 v:w-0 v:bg-brand-purple v:rounded-xl"></div>
        </div>
      </div>
    `

    section.appendChild(loadingContainer)
  }

  async handleProfilesNext(event) {
    const { pets } = event.detail
    const progressBar = document.querySelector('.pet-profiles__progress-bar')
    let completed = false
    let redirected = false

    let animationId = null
    const startTime = Date.now()
    const maxProgress = 95
    const timeConstant = 2000
    let lastProgress = 0

    const animateProgress = () => {
      if (!completed && !redirected && progressBar) {
        const elapsed = Date.now() - startTime
        const progress = Math.round(maxProgress * (1 - Math.exp(-elapsed / timeConstant)) * 10) / 10
        
        if (progress !== lastProgress) {
          progressBar.style.width = `${progress}%`
          lastProgress = progress
        }
        
        animationId = requestAnimationFrame(animateProgress)
      }
    }

    if (progressBar) {
      progressBar.style.transition = 'width 0.3s ease-out'
      progressBar.style.width = '0%'
      void progressBar.offsetWidth
      animationId = requestAnimationFrame(animateProgress)
    }

    const timeoutId = setTimeout(() => {
      if (!completed && !redirected) {
        console.warn('[PetProfiles] Timeout reached - redirecting to cart anyway')
        if (animationId) cancelAnimationFrame(animationId)
        if (progressBar) {
          progressBar.style.transition = 'width 1s ease-out'
          void progressBar.offsetWidth
          progressBar.style.width = '100%'
        }
        setTimeout(() => {
          redirected = true
          window.location.href = '/cart'
        }, 300)
      }
    }, 10000)

    try {
      const result = await processAndApplyRecommendations(pets, this.#boxState)

      if (!result.success) {
        console.error('[PetProfiles] Failed to process pets:', result.error)
        if (animationId) cancelAnimationFrame(animationId)
        clearTimeout(timeoutId)
        return
      }

      completed = true
      clearTimeout(timeoutId)
      if (animationId) cancelAnimationFrame(animationId)

      if (!redirected) {
        if (progressBar) {
          progressBar.style.transition = 'width 1s ease-out'
          void progressBar.offsetWidth
          progressBar.style.width = '100%'
        }

        setTimeout(() => {
          if (!redirected) {
            console.log('[PetProfiles] Redirecting to cart')
            redirected = true
            window.location.href = '/cart'
          }
        }, 300)
      }
    } catch (error) {
      console.error('[PetProfiles] Error in handleProfilesNext:', error)
      if (animationId) cancelAnimationFrame(animationId)
      clearTimeout(timeoutId)
      if (!redirected) {
        redirected = true
        window.location.href = '/cart'
      }
    }
  }
}

if (!customElements.get('pet-profiles')) {
  customElements.define('pet-profiles', PetProfiles)
}

export default PetProfiles